import Service, { service } from "@ember/service";
import { addGlobalNotice } from "discourse/components/global-notice";
import DeprecationWorkflow from "discourse/deprecation-workflow";
import { bind } from "discourse/lib/decorators";
import {
  registerUniversalDeprecationHandler,
  unregisterUniversalDeprecationHandler,
} from "discourse/lib/deprecated";
import identifySource from "discourse/lib/source-identifier";
import { escapeExpression } from "discourse/lib/utilities";
import dDasherize from "discourse/ui-kit/helpers/d-dasherize";
import { i18n } from "discourse-i18n";

const REPLACEMENT_URLS = {};

export default class DeprecationWarningHandler extends Service {
  @service currentUser;
  @service siteSettings;

  #adminWarned = new Set();

  constructor() {
    super(...arguments);

    // `buffered: true` replays deprecations that fired before this service
    // existed, e.g. the boot-time `discourse.hbs-extension` notice.
    registerUniversalDeprecationHandler(this.handle, { buffered: true });
  }

  willDestroy() {
    unregisterUniversalDeprecationHandler(this.handle);
  }

  @bind
  handle(message, opts) {
    if (DeprecationWorkflow.shouldSilence(opts.id)) {
      return;
    }

    const source = opts.source || identifySource();
    if (source?.type === "browser-extension") {
      return;
    }

    this.maybeNotifyAdmin(opts, source);
  }

  maybeNotifyAdmin(opts, source) {
    if (!this.currentUser?.admin) {
      return;
    }

    if (!this.siteSettings.warn_critical_js_deprecations) {
      return;
    }

    if (DeprecationWorkflow.shouldNotifyAdmin(opts.id)) {
      this.notifyAdmin(opts, source);
    }
  }

  notifyAdmin({ id, url }, source) {
    if (this.#adminWarned.has(id)) {
      return;
    }

    this.#adminWarned.add(id);

    if (REPLACEMENT_URLS[id]) {
      url = REPLACEMENT_URLS[id];
    }

    let sourceString;
    if (source?.type === "theme") {
      sourceString = i18n("critical_deprecation.theme_source", {
        name: escapeExpression(source.name),
        path: source.path,
      });
    } else if (source?.type === "plugin") {
      sourceString = i18n("critical_deprecation.plugin_source", {
        name: escapeExpression(source.name),
      });
    } else {
      sourceString = i18n("critical_deprecation.unknown_source");
    }

    let notice =
      i18n("critical_deprecation.notice", {
        source: sourceString,
        id,
      }) + " ";

    if (url) {
      notice += i18n("critical_deprecation.learn_more_link", {
        url,
      });
    }

    if (this.siteSettings.warn_critical_js_deprecations_message) {
      notice += " " + this.siteSettings.warn_critical_js_deprecations_message;
    }

    addGlobalNotice(notice, `critical-deprecation--${dDasherize(id)}`, {
      dismissable: true,
      dismissDuration: moment.duration(1, "day"),
      level: "warn",
    });
  }
}
